
import { defineComponent, ref } from "vue";
import messages from "@/config/messages";
import helper from "@/utils/helper";
import common from "@/config/common";
import routesPath from "@/config/routesPath";
import { mapMutations, mapGetters } from "vuex";
import * as apiClient from "@/apiclient/";
import eventBus from "@/utils/eventBus";
import store from "@/store";

export default defineComponent({
  name: "EnrollmentsView",
  components: {},
  data() {
    return {
      validationMsg: "",
      action: "",
      showModal: false,
      showDeleteModal: false,
      enrollments: [] as any,
      selectedElectionenrollmentId: 0,
      selectedElectionId: 0,
      enrollmentBenefits: [] as string[],
      messages,
      saveBtnTxt: "SKIP & NEXT",
    };
  },
  mounted() {
    this.getAllData();
  },
  beforeRouteLeave(to, from, next) {
    helper.beforeRouterLeave(next);
  },
  computed: {
    ...mapGetters(["getFormDirty", "getEnrollment"]),
  },
  methods: {
    ...mapMutations([
      "setFormDirty",
      "setShowModal",
      "setModalTitle",
      "setModalContent",
      "setEnrollment",
      "setTotalEnrollments",
      "setAutoLogoutMsg",
      "setIsPayed",
      "setPaymentIntents",
    ]),
    getServiceTypeLabel(type: number): string {
      return type === common.serviceType.Cobra ? "Cobra" : "Open";
    },
    isEnrollmentEnded(enrollment: any): boolean {
      if (enrollment.serviceType == 1) {
        return (
          enrollment.openEnrollment.enrollmentEndDate &&
          new Date(enrollment.openEnrollment.enrollmentEndDate) < new Date()
        );
      }

      return (
        enrollment.lastDateToEnroll &&
        new Date(enrollment.lastDateToEnroll) < new Date()
      );
    },
    async startEnrollment(enrollment: apiClient.ServiceDto) {
      if (
        (enrollment.serviceType == 1 && !enrollment.openEnrollment!.started) ||
        (enrollment.serviceType == 0 && !enrollment.cobra!.started)
      ) {
        var response = await apiClient
          .searchParticipantController()
          .postApiSearchparticipantCreateElection({
            customerParticipantId: enrollment.customerParticipantId,
          });

        if (!response || response.error) {
          this.validationMsg =
            "Enrollment processing failed. " + messages.oopsErrorContent;

          return;
        } else {
          this.getAllData();
        }
      }

      store.commit("setEnrollment", enrollment);
      store.commit("setTotalEnrollments", this.enrollments.length);

      if (enrollment.cobra?.status === 10) {
        helper.setPageStep(common.pageStep.confirmation);
        this.$router.push({
          name: routesPath.applicationSuccessful,
        });
        return;
      } else if (
        enrollment.serviceType == 0 &&
        enrollment.cobra &&
        enrollment.cobra.electionID
      ) {
        this.checkIfUserPayedAlready(enrollment.cobra?.electionID);
      }

      this.$router.push({
        name: routesPath.profileInformation,
      });
      return;
    },
    addDayAndFormat(date: string): string {
      const dt = new Date(date);
      dt.setDate(dt.getDate() + 1);
      return helper.formatDate(dt.toISOString());
    },
    formatDate(date: string): string {
      return helper.formatDate(date);
    },
    async getAllData(): Promise<void> {
      const result = await apiClient.userController().getApiUserService();
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.enrollments = result.data.filter(
          (enrollment: any) =>
            !enrollment.openEnrollment ||
            enrollment.openEnrollment.active !== false
        );

        sessionStorage.setItem(
          common.storageVariables.totalEnrollments,
          JSON.stringify(this.enrollments.length)
        );
        this.setTotalEnrollments(this.enrollments.length);

        // Only auto-start if not already enrolled and not ended
        if (this.enrollments.length == 1) {
          const enrollment = this.enrollments[0];
          let enrollmentEnded = false;

          // Use lastDateToEnroll for both service types
          if (
            enrollment.lastDateToEnroll &&
            new Date(enrollment.lastDateToEnroll) < new Date()
          ) {
            enrollmentEnded = true;
          }

          // Check already enrolled status
          const alreadyEnrolled =
            (enrollment.serviceType == 1 &&
              enrollment.openEnrollment &&
              enrollment.openEnrollment.status === 10) ||
            (enrollment.serviceType == 0 &&
              enrollment.cobra &&
              enrollment.cobra.status === 10);

          if (!enrollmentEnded && !alreadyEnrolled) {
            this.setFormDirty(false);
            this.setAutoLogoutMsg("");

            if (
              (enrollment.serviceType == 1 &&
                !enrollment.openEnrollment.started) ||
              (enrollment.serviceType == 0 && !enrollment.cobra.started)
            ) {
              var response = await apiClient
                .searchParticipantController()
                .postApiSearchparticipantCreateElection({
                  customerParticipantId: enrollment.customerParticipantId,
                });

              if (!response || response.error) {
                this.validationMsg =
                  "Enrollment processing failed. " + messages.oopsErrorContent;
                return;
              }
            }

            sessionStorage.setItem(
              common.storageVariables.enrollment,
              JSON.stringify(enrollment)
            );
            store.commit("setEnrollment", enrollment);
            eventBus.emit("toggle-enrollments-menu", false);

            if (enrollment.cobra?.status === 10) {
              helper.setPageStep(common.pageStep.confirmation);
              this.$router.push({
                name: routesPath.applicationSuccessful,
              });
              return;
            } else if (enrollment.serviceType == 0) {
              this.checkIfUserPayedAlready(enrollment.cobra?.electionID);
            }

            this.$router.push({
              name: routesPath.profileInformation,
            });
          }
        }
      }
    },
    getDaysRemainingMessage(enrollment: apiClient.ServiceDto): string {
      if (enrollment.serviceType == 0) {
        return enrollment.daysRemaining + " day(s) remaining.";
      }

      var dateDiffs = this.getTimeDifference(
        new Date(),
        new Date(enrollment.openEnrollment!.enrollmentEndDate!)
      );

      if (dateDiffs.days > 0) {
        return dateDiffs.days + " day(s) remaining.";
      }

      if (dateDiffs.hours > 0) {
        var message = dateDiffs.hours + " hour(s)";
        if (dateDiffs.minutes > 0) {
          message += " and " + dateDiffs.minutes + " minute(s) remaining.";
        } else {
          message += " remaining.";
        }

        return message;
      }

      if (dateDiffs.minutes > 0) {
        return dateDiffs.minutes + " minute(s) remaining.";
      }

      return (
        "Enrollment ended at " +
        helper.formatDate(
          enrollment.openEnrollment!.enrollmentEndDate!.toString(),
          "MM/DD/YYYY"
        ) +
        " " +
        new Date(
          enrollment.openEnrollment!.enrollmentEndDate!
        ).toLocaleTimeString(undefined, { timeStyle: "short" })
      );
    },
    getTimeDifference(date1: Date, date2: Date) {
      // Difference in milliseconds
      const diffMs = date2.getTime() - date1.getTime();

      // Constants for time conversion
      const MS_PER_MINUTE = 1000 * 60;
      const MS_PER_HOUR = MS_PER_MINUTE * 60;
      const MS_PER_DAY = MS_PER_HOUR * 24;

      // Compute full days, hours, and minutes
      const days = Math.floor(diffMs / MS_PER_DAY);
      const hours = Math.floor((diffMs % MS_PER_DAY) / MS_PER_HOUR);
      const minutes = Math.floor((diffMs % MS_PER_HOUR) / MS_PER_MINUTE);

      return {
        days,
        hours,
        minutes,
      };
    },
    async downloadConfirmationAsPdf(customerParticipantId: number) {
      var params = {
        customerParticipantId: customerParticipantId,
      };

      const result = await apiClient
        .confirmationController()
        .getApiConfirmationGetFile(params);

      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      }

      if (!result.data) {
        return;
      }
      const blob = new Blob([helper.base64ToUint8Array(result.data)], {
        type: "application/pdf",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = "summary.pdf";
      link.click();

      // Clean up the URL and link element
      window.URL.revokeObjectURL(url);
    },
    async checkIfUserPayedAlready(electionID: number) {
      var completePaymentResponse = await apiClient
        .stripePaymentGatewayController()
        .getApiStripepaymentgatewayGetCompletePayments({
          electionId: electionID,
        });

      if (
        completePaymentResponse.data &&
        completePaymentResponse.data.length > 0
      ) {
        this.setIsPayed(true);
        this.setPaymentIntents(completePaymentResponse.data);
      } else {
        this.setIsPayed(false);
      }
    },
  },
});
